import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './TrackingPage.css';

const API_URL = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost'
  ? 'http://localhost:3001'
  : 'https://api.33tracking.com';

// ── Deterministic seeded random number generator ──
// Uses tracking number as seed so timeline is identical on every page load
function createSeededRandom(seed) {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  let state = Math.abs(hash) || 1;
  return function () {
    state = (state * 1664525 + 1013904223) & 0xffffffff;
    return (state >>> 0) / 0xffffffff;
  };
}

// ── All possible pre-transit timeline steps ──
const PRE_TRANSIT_STEPS = [
  { id: 'order_placed', message: 'Order Placed', group: 'order' },
  { id: 'payment_confirmed', message: 'Payment Confirmed', group: 'order' },
  { id: 'order_verified', message: 'Order Verified', group: 'order' },
  { id: 'order_approved', message: 'Order Approved', group: 'order' },
  { id: 'processing_started', message: 'Processing Started', group: 'processing' },
  { id: 'items_allocated', message: 'Items Allocated', group: 'processing' },
  { id: 'awaiting_stock', message: 'Awaiting Stock', group: 'processing' },
  { id: 'preparing_fulfillment', message: 'Preparing for Fulfillment', group: 'fulfillment' },
  { id: 'picking_items', message: 'Picking Items', group: 'fulfillment' },
  { id: 'packing_started', message: 'Packing Started', group: 'fulfillment' },
  { id: 'quality_check', message: 'Quality Check', group: 'fulfillment' },
  { id: 'packed', message: 'Packed', group: 'fulfillment' },
  { id: 'label_created', message: 'Shipping Label Created', group: 'shipping' },
  { id: 'ready_dispatch', message: 'Ready for Dispatch', group: 'shipping' },
  { id: 'manifested', message: 'Manifested', group: 'shipping' },
  { id: 'pickup_scheduled', message: 'Pickup Scheduled', group: 'shipping' },
  { id: 'picked_up', message: 'Picked Up by Courier', group: 'handoff' },
  { id: 'handed_over', message: 'Handed Over to Carrier', group: 'handoff' },
  { id: 'accepted_carrier', message: 'Accepted by Carrier', group: 'handoff' },
];

// Required steps that always appear (anchors for realism)
const REQUIRED_STEP_IDS = [
  'order_placed',
  'payment_confirmed',
  'processing_started',
  'packed',
  'label_created',
  'picked_up',
  'accepted_carrier',
];

// ── Event description messages ──
// Major events get 2-line messages, minor events get 1-line or none
// eslint-disable-next-line no-unused-vars
const EVENT_MESSAGES = {
  'order_placed': 'Your order has been successfully placed. We\'ve received it and will start processing right away!',
  'payment_confirmed': 'Payment has been confirmed. Your order is being prepared for shipment.',
  'order_verified': 'Your order has been verified and approved for processing.',
  'order_approved': 'Your order has been approved and is ready for fulfillment.',
  'confirmed': 'Your order has been confirmed and we\'re preparing it for shipment. Thank you for your purchase!',
  'processing_started': 'We\'ve started processing your order and will have it ready soon.',
  'items_allocated': 'Items have been allocated from inventory for your order.',
  'awaiting_stock': 'We\'re waiting for items to be in stock before we can process your order.',
  'preparing_fulfillment': 'Your order is being prepared for fulfillment. We\'re getting it ready to ship!',
  'picking_items': 'Our team is picking items from our warehouse for your order.',
  'packing_started': 'We\'ve started packing your order for shipment.',
  'quality_check': 'Your order is undergoing quality checks before shipping.',
  'packed': 'Your order has been packed and is ready for shipment.',
  'picked_up': 'Your package has been picked up by the courier and is on its way. Tracking updates coming soon!',
  'in_transit': 'Package is moving through courier network.!',
  'accepted_carrier': 'Your shipment has been accepted by the carrier and entered into their system for delivery.',
  'label_created': 'Shipping label has been created and your package is ready for pickup.',
  'ready_dispatch': 'Your order is ready for dispatch to the carrier.',
  'manifested': 'Your order has been manifested and is ready for pickup.',
  'pickup_scheduled': 'Pickup has been scheduled with the courier.',
  'out_for_delivery': 'Your package is out for delivery today. It should arrive shortly!',
  'delivered': 'Your package has been successfully delivered. Thank you for shopping with us!',
  // In Transit sub-events messages
  'arrived_origin_facility': 'Arrived at origin facility',
  'processed_origin': 'Processed at origin facility',
  'departed_sort': 'Departed from sort facility',
  'in_transit_facility': 'In transit to next facility',
  'arrived_airport': 'Arrived at airport',
  'flight_scheduled': 'Flight scheduled',
  'flight_departed': 'Flight departed',
  'in_air': 'In air transit',
  'flight_arrived': 'Flight arrived',
  'received_hub': 'Received at hub',
  'customs_processing': 'In customs clearance processing',
  'customs_verified': 'Customs verified',
  'clearance_completed': 'Clearance completed',
  'noa_received': 'Notification of arrival received',
  'released_customs': 'Released from customs',
  'arrived_destination_hub': 'Arrived at destination hub',
  'departed_hub': 'Departed from hub',
  'arrived_regional_center': 'Arrived at regional center',
  'processing_delivery': 'Processing for delivery',
  'dispatch_delivery': 'Dispatched for delivery',
  'pickup_notice': 'Pickup notice left',
  'redelivery': 'Redelivery scheduled'
};

// ── In Transit sub-events (25 steps for the shipping journey) ──
const IN_TRANSIT_STEPS = [
  { id: 'in_transit', message: 'In Transit', isMain: true },
  { id: 'arrived_origin_facility', message: 'Arrived at Origin Facility' },
  { id: 'processed_origin', message: 'Package Processed at Origin Facility' },
  { id: 'departed_sort', message: 'Departed from Origin Sort Facility' },
  { id: 'in_transit_facility', message: 'Shipment in Transit to Next Facility' },
  { id: 'arrived_airport', message: 'Arrived at Origin International Airport' },
  { id: 'flight_scheduled', message: 'International Flight Scheduled' },
  { id: 'flight_departed', message: 'International Flight has Departed' },
  { id: 'in_air', message: 'In Air Transit to Destination Country' },
  { id: 'flight_arrived', message: 'International Flight has Arrived' },
  { id: 'received_hub', message: 'Package Received at International Hub' },
  { id: 'customs_processing', message: 'In Customs Clearance Processing - Import' },
  { id: 'customs_verified', message: 'Customs Documents Verified' },
  { id: 'clearance_completed', message: 'Clearance Processing Completed - Import' },
  { id: 'noa_received', message: 'NOA Received (Notification of Arrival)' },
  { id: 'released_customs', message: 'Package Released from Customs' },
  { id: 'arrived_destination_hub', message: 'Arrived at Destination Country Hub' },
  { id: 'transferred_local', message: 'Package Transferred to Local Carrier' },
  { id: 'received_at_hub', message: 'We\'ve Received this Parcel at the Hub' },
  { id: 'sorted_region', message: 'Parcel Sorted for Delivery Region' },
  { id: 'processed_hub', message: 'Parcel Processed at Regional Hub' },
  { id: 'processed_depot', message: 'Parcel Processed at Local Delivery Depot' },
  { id: 'assigned_route', message: 'Parcel Assigned to Delivery Route' },
  { id: 'courier_received', message: 'The Courier has Received this Parcel' },
  { id: 'delivery_scheduled', message: 'Delivery Time Slot Scheduled' },
  { id: 'ready_delivery', message: 'Ready for Final Delivery' },
];

/**
 * DEPRECATED: Generate In Transit timeline events
 * Now we only show database events, so this is no longer used
 * @param {string} trackingNumber - used as seed for deterministic randomness
 * @param {string} shippedAt - ISO date when shipped (in transit started)
 * @param {string|null} estimatedDeliveryAt - ISO date of estimated delivery
 * @returns {Array} timeline events with { message, timestamp, status }
 */
// eslint-disable-next-line no-unused-vars
function generateInTransitTimeline(trackingNumber, shippedAt, estimatedDeliveryAt) {
  if (!shippedAt) return [];
  
  const rand = createSeededRandom(trackingNumber + '_transit');
  const startDate = new Date(shippedAt);
  const now = new Date();
  
  // If no estimated delivery, default to 12-21 days from shipped date
  let deliveryDate;
  if (estimatedDeliveryAt) {
    deliveryDate = new Date(estimatedDeliveryAt);
  } else {
    const minDays = 12;
    const maxDays = 21;
    const randomDays = minDays + Math.floor(rand() * (maxDays - minDays));
    deliveryDate = new Date(startDate);
    deliveryDate.setDate(deliveryDate.getDate() + randomDays);
  }
  
  const transitDurationMs = deliveryDate.getTime() - startDate.getTime();
  if (transitDurationMs <= 0) return [];
  
  // Calculate how many days we are into transit
  const transitDays = transitDurationMs / (24 * 60 * 60 * 1000);
  
  // Select which sub-events to include based on transit duration
  // More days = more events (13-25 events)
  let selectedCount = Math.max(13, Math.ceil(IN_TRANSIT_STEPS.length * (Math.min(transitDays, 21) / 21)));
  selectedCount = Math.min(selectedCount, IN_TRANSIT_STEPS.length);
  
  // Always include the first "In Transit" main event, then pick from the rest
  const selectedEvents = [IN_TRANSIT_STEPS[0]]; // "In Transit" main event
  const optionalSteps = IN_TRANSIT_STEPS.slice(1);
  
  // Pick events evenly distributed with some randomness
  const step = optionalSteps.length / (selectedCount - 1);
  for (let i = 0; i < selectedCount - 1; i++) {
    const idx = Math.floor(i * step + rand() * step * 0.5);
    if (idx < optionalSteps.length && !selectedEvents.find(e => e.id === optionalSteps[idx].id)) {
      selectedEvents.push(optionalSteps[idx]);
    }
  }
  
  // Generate timestamps spread across the transit period
  // Add 12 hour offset so In Transit events start WELL AFTER pre-transit events end
  const offsetMs = 12 * 60 * 60 * 1000; // 12 hours after shipped time

  const events = [];
  for (let i = 0; i < selectedEvents.length; i++) {
    const progress = i / (selectedEvents.length - 1 || 1); // 0 to 1
    const eventTime = new Date(startDate.getTime() + offsetMs + progress * transitDurationMs);
    
    // Add minimal randomness to event timing (±1% of transit duration) to preserve order
    const jitter = (rand() - 0.5) * transitDurationMs * 0.02;
    eventTime.setTime(eventTime.getTime() + jitter);
    
    // Only include events that have "happened" (before now)
    if (eventTime <= now) {
      events.push({
        status: selectedEvents[i].id,
        message: selectedEvents[i].message,
        timestamp: eventTime.toISOString(),
        isInTransit: true,
      });
    }
  }
  
  // Sort by timestamp
  events.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  
  return events;
}

/**
 * DEPRECATED: Generate a deterministic, realistic pre-transit timeline.
 * Now we only show database events, so this is no longer used
 * @param {string} trackingNumber - used as random seed
 * @param {string} orderCreatedAt - ISO date string
 * @param {string|null} timelineAnchor - ISO date to end auto-timeline (e.g., confirmed event time)
 * @returns {Array} timeline events with { message, timestamp, status }
 */
// eslint-disable-next-line no-unused-vars
function generateAutoTimeline(trackingNumber, orderCreatedAt, timelineAnchor) {
  // These events represent the "Getting Ready" phase before shipping begins

  const rand = createSeededRandom(trackingNumber);
  const orderDate = new Date(orderCreatedAt);
  const now = new Date();
  
  // Use the provided anchor date (e.g., confirmed event), or now if not provided
  // This ensures auto-generated events end BEFORE the confirmed event
  const endDate = timelineAnchor ? new Date(timelineAnchor) : now;
  
  // Make sure endDate is after orderDate
  if (endDate.getTime() <= orderDate.getTime()) {
    return []; // Can't generate timeline if anchor is before order creation
  }
  
  // Total time window: from order creation to shipped (or now)
  const elapsedMs = endDate.getTime() - orderDate.getTime();
  const spreadMs = elapsedMs;

  if (spreadMs <= 0) return [];

  // Pick which optional steps to include (always include required ones)
  const optionalSteps = PRE_TRANSIT_STEPS.filter(s => !REQUIRED_STEP_IDS.includes(s.id));
  const selectedOptional = optionalSteps.filter(() => rand() < 0.4); // ~40% chance each
  
  // Merge required + selected optional, maintain original order
  const allStepIds = new Set([...REQUIRED_STEP_IDS, ...selectedOptional.map(s => s.id)]);
  const selectedSteps = PRE_TRANSIT_STEPS.filter(s => allStepIds.has(s.id));

  // Ensure we have 8-14 steps
  while (selectedSteps.length < 8) {
    const remaining = PRE_TRANSIT_STEPS.filter(s => !allStepIds.has(s.id));
    if (remaining.length === 0) break;
    const pick = remaining[Math.floor(rand() * remaining.length)];
    allStepIds.add(pick.id);
    // Re-insert in correct position
    const idx = PRE_TRANSIT_STEPS.findIndex(s => s.id === pick.id);
    let insertAt = selectedSteps.findIndex((s) => {
      const origIdx = PRE_TRANSIT_STEPS.findIndex(ps => ps.id === s.id);
      return origIdx > idx;
    });
    if (insertAt === -1) insertAt = selectedSteps.length;
    selectedSteps.splice(insertAt, 0, pick);
  }

  // Generate random time gaps (weights) for each step
  // Handoff events (last 3: picked_up, handed_over, accepted_carrier) should be clustered at the end
  const handoffIds = ['picked_up', 'handed_over', 'accepted_carrier'];
  
  const weights = selectedSteps.map((step, idx) => {
    if (handoffIds.includes(step.id)) {
      return 0.1 + rand() * 0.2; // Small weight for handoff events (clustered together)
    }
    return 0.5 + rand() * 1.5; // Normal weight for other events
  });

  // Reserve the last 5% of time for handoff events
  const mainSpreadMs = spreadMs * 0.95;
  const handoffStartMs = spreadMs * 0.95;

  // Assign timestamps
  const events = [];
  let cumulativeMs = 0;
  let handoffIndex = 0;

  // Count handoff events
  const handoffCount = selectedSteps.filter(s => handoffIds.includes(s.id)).length;
  const nonHandoffSteps = selectedSteps.filter(s => !handoffIds.includes(s.id));
  const nonHandoffWeights = weights.filter((_, i) => !handoffIds.includes(selectedSteps[i].id));
  const totalNonHandoffWeight = nonHandoffWeights.reduce((a, b) => a + b, 0);

  for (let i = 0; i < selectedSteps.length; i++) {
    const step = selectedSteps[i];
    
    // First step starts a few minutes after order creation
    if (i === 0) {
      const offsetMs = Math.floor(rand() * 3 * 60 * 1000); // 0-3 min
      events.push({
        status: step.id,
        message: step.message,
        timestamp: new Date(orderDate.getTime() + offsetMs).toISOString(),
      });
      continue;
    }

    let eventTime;
    
    if (handoffIds.includes(step.id)) {
      // Handoff events: cluster in the last 5% of time, spaced evenly
      const handoffProgress = handoffIndex / Math.max(handoffCount - 1, 1);
      const handoffTimeMs = handoffStartMs + handoffProgress * (spreadMs - handoffStartMs);
      eventTime = new Date(orderDate.getTime() + handoffTimeMs);
      handoffIndex++;
    } else {
      // Normal events: spread across the first 95% of time
      const nonHandoffIdx = nonHandoffSteps.findIndex(s => s.id === step.id);
      if (nonHandoffIdx > 0) {
        const progressWeight = nonHandoffWeights.slice(0, nonHandoffIdx + 1).reduce((a, b) => a + b, 0);
        cumulativeMs = (progressWeight / totalNonHandoffWeight) * mainSpreadMs;
      }
      eventTime = new Date(orderDate.getTime() + cumulativeMs);
    }

    // Only show events that have "happened" (before the anchor point or now)
    // For auto-generated events, this prevents them from extending past the confirmed event
    if (eventTime.getTime() < endDate.getTime()) {
      events.push({
        status: step.id,
        message: step.message,
        timestamp: eventTime.toISOString(),
      });
    }
  }

  return events;
}

// Parse a flexible pre-order release date (Q2-2026, DD-MM-YYYY, DD-Month-YYYY, native)
function parsePreorderDate(dateStr) {
  if (!dateStr) return null;
  try {
    const cleaned = String(dateStr).trim().toLowerCase();
    const q = cleaned.match(/^q([1-4])-(\d{4})/);
    if (q) return new Date(parseInt(q[2], 10), (parseInt(q[1], 10) - 1) * 3, 1);
    const dmy = cleaned.match(/(\d{1,2})-(\d{1,2})-(\d{4})/);
    if (dmy) return new Date(parseInt(dmy[3], 10), parseInt(dmy[2], 10) - 1, parseInt(dmy[1], 10));
    const months = ['january','february','march','april','may','june','july','august','september','october','november','december'];
    const named = cleaned.match(new RegExp(`(\\d{1,2})[\\s-](${months.join('|')})[\\s-]+(\\d{4})`, 'i'));
    if (named) { const mi = months.indexOf(named[2].toLowerCase()); if (mi >= 0) return new Date(parseInt(named[3], 10), mi, parseInt(named[1], 10)); }
    const nat = new Date(cleaned);
    if (!isNaN(nat.getTime())) return nat;
    return null;
  } catch { return null; }
}

function TrackingPage() {
  const { trackingNumber } = useParams();
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTrackingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingNumber]);

  const fetchTrackingData = async () => {
    try {
      setError(null);
      const response = await axios.get(
        `${API_URL}/api/track/${trackingNumber}`
      );
      
      if (!response.data.success || !response.data.tracking) {
        setError(true);
        setLoading(false);
        return;
      }

      // Map our backend response to the format the UI expects
      const tracking = response.data.tracking;

      // Get events from DB - ONLY show database events, nothing synthetic
      let dbEvents = (response.data.timeline || []);

      // Sort by timestamp DESCENDING (newest first) for display
      dbEvents.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      const mergedTimeline = dbEvents;

      // Use line_items from API response, fallback to legacy single item
      let lineItems = response.data.line_items || [];
      if (lineItems.length === 0 && tracking.product_title) {
        lineItems = [{
          title: tracking.product_title,
          quantity: tracking.product_qty || 1
        }];
      }

      const modifiedResponse = {
        success: true,
        shipment: {
          tracking_number: tracking.tracking_number,
          order_number: tracking.order_number,
          order_id: tracking.order_id,
          fulfillment_id: tracking.fulfillment_id,
          status: tracking.timeline_status,
          product_title: tracking.product_title,
          product_qty: tracking.product_qty,
          order_created_at: tracking.order_created_at,
          order_shipped_at: tracking.order_shipped_at,
          estimated_delivery_at: tracking.estimated_delivery_at,
          is_preorder: tracking.is_preorder,
          preorder_release_date: tracking.preorder_release_date,
          line_items: JSON.stringify(lineItems)
        },
        timeline: mergedTimeline
      };
      
      setShipment(modifiedResponse);
      setLoading(false);
    } catch (err) {
      setError(true);
      setLoading(false);
    }
  };

  const getStepInfo = (status) => {
    const steps = {
      created: { name: 'Getting Ready', bg: '#e3f2fd', color: '#1976d2', icon: 'https://img.icons8.com/comic/100/move-by-trolley.png' },
      label_printed: { name: 'Getting Ready', bg: '#e3f2fd', color: '#1976d2', icon: 'https://img.icons8.com/comic/100/move-by-trolley.png' },
      picked_up: { name: 'Getting Ready', bg: '#e3f2fd', color: '#1976d2', icon: 'https://img.icons8.com/comic/100/move-by-trolley.png' },
      confirmed: { name: 'Confirmed', bg: '#e3f2fd', color: '#1976d2', icon: 'https://img.icons8.com/comic/100/move-by-trolley.png' },
      in_transit: { name: 'In Transit', bg: '#e3f2fd', color: '#1976d2', icon: 'https://img.icons8.com/comic/100/in-transit.png' },
      out_for_delivery: { name: 'Out for Delivery', bg: '#fff3e0', color: '#f57c00', icon: 'https://img.icons8.com/comic/100/delivery.png' },
      delivered: { name: 'Delivered', bg: '#e8f5e9', color: '#388e3c', icon: 'https://img.icons8.com/comic/100/package.png' },
      failed: { name: 'Delivery Failed', bg: '#ffebee', color: '#d32f2f', icon: 'https://img.icons8.com/external-line-vectorslab/68/external-Parcel-Error-shopping-and-commerce-line-vectorslab.png' },
      returned: { name: 'Returned', bg: '#ffebee', color: '#d32f2f', icon: 'https://img.icons8.com/external-nawicon-detailed-outline-nawicon/64/external-24-Return-Box-ecommerce-nawicon-detailed-outline-nawicon.png' }
    };
    return steps[status] || steps.created;
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    const options = { month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const formatLastUpdate = (timeline) => {
    if (!timeline || timeline.length === 0) return 'No updates yet';
    const lastEvent = timeline[0]; // First item (sorted newest first)
    const date = new Date(lastEvent.timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    
    const options = { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return date.toLocaleDateString('en-US', options);
  };

  const parseLineItems = (lineItemsStr) => {
    try {
      if (typeof lineItemsStr === 'string') {
        return JSON.parse(lineItemsStr);
      }
      return lineItemsStr || [];
    } catch {
      return [];
    }
  };

  if (loading) {
    return (
      <div className="tracking-page">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading tracking information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tracking-page">
        <div className="error-container">
          <h1>Whoops!</h1>
          <p>We can't seem to find what you're looking for.</p>
          <p className="error-sub">You can find the tracking link in your order summary, or contact us.</p>
        </div>
      </div>
    );
  }

  const stepInfo = getStepInfo(shipment.shipment.status);
  const lineItems = parseLineItems(shipment.shipment.line_items);

  // ── Pre-order state ── before the release date, show a holding state instead
  // of the normal shipping status (the item can't ship yet).
  const preorderReleaseDate = parsePreorderDate(shipment.shipment.preorder_release_date);
  const isPreorderPending = !!(shipment.shipment.is_preorder && preorderReleaseDate && preorderReleaseDate > new Date());
  const displayStep = isPreorderPending
    ? { name: 'Pre-order', bg: '#f3e8ff', color: '#7c3aed', icon: 'https://img.icons8.com/comic/100/calendar.png' }
    : stepInfo;

  const formatTimelineTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getDeliveryMessage = (status) => {
    if (status === 'delivered') {
      return 'Delivered by 33 Tracking';
    }
    return 'Order will be delivered by Japan Post.';
  };

  const getProgressBarImage = (stepIndex, currentStepIndex) => {
    if (stepIndex < currentStepIndex) {
      return 'https://tracker.flexport.com/build/_assets/horizontalComplete-N5Q4Q7DP.svg';
    } else if (stepIndex === currentStepIndex) {
      return 'https://tracker.flexport.com/build/_assets/horizontalProgress-W4R235H5.svg';
    } else {
      return 'https://tracker.flexport.com/build/_assets/horizontalNotStarted-AZMVRQQA.svg';
    }
  };

  const getCurrentStepIndex = (status) => {
    const statusMap = {
      created: 0,
      label_printed: 0,
      picked_up: 0,
      in_transit: 1,
      out_for_delivery: 2,
      delivered: 3,
      failed: 3,
      returned: 3
    };
    return statusMap[status] || 0;
  };

  return (
    <div className="tracking-page">

      {/* Header */}
      <div className="tracking-header">
        <div className="header-content">
          <div className="courier-name">33 Tracking</div>
          <div className="order-info">
            <span>Order #{shipment.shipment.fulfillment_id ? `${String(shipment.shipment.fulfillment_id).split('/').pop()}-${shipment.shipment.order_id}` : shipment.shipment.order_id}</span>
          </div>
        </div>
      </div>

      {/* Status Card */}
      <div className="status-card" style={{ backgroundColor: displayStep.bg }}>
        <div className="status-content">
          <div className="status-text">
            <h2 style={{ color: displayStep.color }}>{displayStep.name}</h2>
            <p className="last-update">Last update: {formatLastUpdate(shipment.timeline)}</p>
            {isPreorderPending && (
              <p className="expected-date">
                Ships after {formatShortDate(preorderReleaseDate)} — we begin processing on release day.
              </p>
            )}
            {!isPreorderPending && ['in_transit', 'out_for_delivery'].includes(shipment.shipment.status) && shipment.shipment.estimated_delivery_at && (
              <p className="expected-date">
                Expected delivery by {formatShortDate(shipment.shipment.estimated_delivery_at)}
              </p>
            )}
            {!isPreorderPending && ['in_transit', 'out_for_delivery'].includes(shipment.shipment.status) && !shipment.shipment.estimated_delivery_at && (
              <p>Delivery date to be confirmed.</p>
            )}
          </div>
          <div className="status-icon">
            {displayStep.icon.startsWith('http') ? (
              <img
                src={displayStep.icon}
                alt={displayStep.name}
                width={shipment.shipment.status === 'failed' ? '80' : '100'}
                height={shipment.shipment.status === 'failed' ? '80' : '100'}
              />
            ) : (
              <span>{displayStep.icon}</span>
            )}
          </div>
        </div>

        {/* Progress Bar with 4 Parts */}
        <div className="progress-section">
          <div className="progress-bar">
            <div className="progress-bar-item">
              <img 
                src={getProgressBarImage(0, getCurrentStepIndex(shipment.shipment.status))} 
                alt="Step 1"
              />
            </div>
            <div className="progress-bar-item">
              <img 
                src={getProgressBarImage(1, getCurrentStepIndex(shipment.shipment.status))} 
                alt="Step 2"
              />
            </div>
            <div className="progress-bar-item">
              <img 
                src={getProgressBarImage(2, getCurrentStepIndex(shipment.shipment.status))} 
                alt="Step 3"
              />
            </div>
            <div className="progress-bar-item">
              <img 
                src={getProgressBarImage(3, getCurrentStepIndex(shipment.shipment.status))} 
                alt="Step 4"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="tracking-container">
        <div className="timeline">
          {shipment.timeline && shipment.timeline.length > 0 ? (
            <>
              <div className="timeline-events">
                {shipment.timeline.map((event, index) => {
                  const dateObj = new Date(event.timestamp);
                  const dateStr = dateObj.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                  });
                  const timeStr = formatTimelineTime(event.timestamp);
                  
                  // The message field now contains the display name directly from backend
                  // e.g., "Order Created", "Payment Confirmed", "In Transit", etc.
                  const headingName = event.message || event.status;
                  
                  // For major events, we can show a description from EVENT_MESSAGES
                  // For sub events, we don't need a separate description (heading IS the description)
                  const isMajorEvent = event.event_type === 'major';
                  const description = isMajorEvent ? (EVENT_MESSAGES[event.status] || '') : '';

                  return (
                    <div key={index} className={`timeline-item-wrapper ${event.event_type === 'sub' ? 'timeline-sub-event' : ''}`}>
                      <div className="timeline-marker-container">
                        {index === 0 ? (
                          // Current/Latest event - filled circle with icon
                          <svg className="timeline-marker timeline-marker-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
                            <path fill="rgb(22, 163, 74)" d="M320 576C461.4 576 576 461.4 576 320C576 178.6 461.4 64 320 64C178.6 64 64 178.6 64 320C64 461.4 178.6 576 320 576zM320 224C373 224 416 267 416 320C416 373 373 416 320 416C267 416 224 373 224 320C224 267 267 224 320 224z"/>
                          </svg>
                        ) : (
                          // Completed events - just a simple dot (no icon)
                          <div className="timeline-marker-dot"></div>
                        )}
                      </div>
                      <div className="timeline-details-container">
                        <div className="timeline-first-row">
                          <h2 className={`timeline-event-message ${event.event_type === 'sub' ? 'timeline-sub-message' : ''}`}>{headingName}</h2>
                          <div className="timeline-event-time">
                            <span className="timeline-event-date">{dateStr}</span>
                            <span className="timeline-event-clock">{timeStr}</span>
                          </div>
                        </div>
                        {description && (
                          <p className="timeline-event-description">{description}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : null}
        </div>

        {/* Shipment Details */}
        <div className="shipment-details">
          <div className="shipment-logo">
            <img src="https://www.japanpost.jp/en/group/map/images/index03_img_01.gif" alt="Japan Post" />
          </div>
          <h3>
            <div className="shipment-heading">
              <span>{getDeliveryMessage(shipment.shipment.status)}</span>
            </div>
            <span className="tracking-number-right">{shipment.shipment.tracking_number}</span>
          </h3>
        </div>

        {/* Order Items Box */}
        <div className="order-items-box">
          <div className="order-items-header">
            <span>Order Items</span>
          </div>
          
          {lineItems.length > 0 ? (
            lineItems.map((item, index) => (
              <div key={index} className="order-item-card">
                <div className="order-item-left">
                  <div className="order-item-icon">
                    <img 
                      width="100" 
                      height="100" 
                      src="https://img.icons8.com/comic/100/package.png" 
                      alt="package"
                    />
                  </div>
                  <div className="order-item-details">
                    <div className="order-item-title">{item.title}</div>
                  </div>
                </div>
                <div className="order-item-date">Qty {item.quantity}</div>
              </div>
            ))
          ) : (
            <div style={{ padding: '20px 24px', color: '#999', textAlign: 'center' }}>
              No items found
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TrackingPage;
